import { apiFetchJson } from '@wundo/client/api.ts';
import type {
  ConversationOverview,
  DirectorDecision,
  MessagesApiResponse,
  UserOverview,
} from '@/types/messages.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';

/** One page of a transcript, as the console has always asked for it. */
export const MESSAGE_PAGE_SIZE = 50;

/**
 * How many people the sidebar lists. The list is not paged — it is the whole
 * set of people who have ever said anything, newest first — so it asks for the
 * endpoint's maximum in one go, exactly as the Vue view did.
 */
export const USER_OVERVIEW_LIMIT = 1000;

/** One conversation's messages, newest first — the order the endpoint answers in. */
export function listConversationMessages(
  conversationId: string,
  page = 1,
): Promise<MessagesApiResponse> {
  const params = new URLSearchParams({
    conversation_id: conversationId,
    limit: String(MESSAGE_PAGE_SIZE),
    page: String(page),
  });

  return apiFetchJson<MessagesApiResponse>(`/messages?${params.toString()}`);
}

/** Everyone who has said something, with how much and how recently. */
export function listUsersOverview(
  page = 1,
  limit = USER_OVERVIEW_LIMIT,
  organisationId?: string,
): Promise<PaginatedResponse<UserOverview>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (organisationId) params.set('organisation_id', organisationId);

  return apiFetchJson<PaginatedResponse<UserOverview>>(
    `/messages/overview?${params.toString()}`,
  );
}

/** Every conversation one person has had, across every channel. */
export function listConversations(userId: string): Promise<ConversationOverview[]> {
  return apiFetchJson<ConversationOverview[]>(
    `/messages/conversations?user_id=${encodeURIComponent(userId)}`,
  );
}

/** Every decision the director of one spoken practice made, oldest first. */
export function listDirectorDecisions(
  conversationId: string,
): Promise<DirectorDecision[]> {
  return apiFetchJson<DirectorDecision[]>(
    `/messages/director-decisions?conversation_id=${encodeURIComponent(conversationId)}`,
  );
}
